// Client-side image compression + upload.
//
// `compressImageFile` resizes (800px max width, 0.7 JPEG / PNG passthrough)
// and resolves to `{ dataURL, width, height }`. The dimensions are needed
// by callers that compute aspect-ratio-derived row widths so multi-image
// rows render at uniform heights without cropping.
//
// `uploadCompressedImage` compresses then POSTs the resulting blob to the
// newsletter's images endpoint, returning `{ url, width, height }`. Use
// this for delivered-email image sources; `data:` URLs are rejected or
// stripped by many email clients.

export async function uploadCompressedImage(file, uploadUrl) {
  if (!uploadUrl) throw new Error("uploadCompressedImage: missing uploadUrl")
  const compressed = await compressImageFile(file)
  const blob = await (await fetch(compressed.dataURL)).blob()
  const form = new FormData()
  // Preserve original filename + a sensible extension. Compression chose
  // PNG or JPEG based on the source MIME.
  const ext = blob.type === "image/png" ? "png" : "jpg"
  const baseName = (file.name || "image").replace(/\.[^/.]+$/, "")
  form.append("file", blob, `${baseName}.${ext}`)
  const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
  const response = await fetch(uploadUrl, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Accept": "application/json",
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
    },
    body: form,
  })
  if (!response.ok) {
    throw new Error(`Image upload failed (${response.status})`)
  }
  const payload = await response.json().catch(() => ({}))
  if (!payload.url) throw new Error("Image upload response missing URL")
  return { url: payload.url, width: compressed.width, height: compressed.height }
}

export function compressImageFile(file, options = {}) {
  const maxWidth = options.maxWidth ?? 800
  const quality = options.quality ?? 0.7
  const isPng = (file.type || "").toLowerCase().includes("png")

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const cleanup = () => URL.revokeObjectURL(url)
    const image = new Image()
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas")
        let width = image.width
        let height = image.height
        if (width > maxWidth) {
          height = Math.round(height * (maxWidth / width))
          width = maxWidth
        }
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(image, 0, 0, width, height)
        const dataURL = isPng
          ? canvas.toDataURL("image/png")
          : canvas.toDataURL("image/jpeg", quality)
        cleanup()
        resolve({ dataURL, width, height })
      } catch (err) {
        cleanup()
        reject(err)
      }
    }
    image.onerror = (err) => {
      cleanup()
      reject(err)
    }
    image.src = url
  })
}
